import { useRef, useState } from "react";
import { api, unwrap } from "../api/client";
import type { OutputView } from "../api/types";
import { N_, t } from "../lib/i18n";
import { Button, Checkbox, ConfirmDialog, Field, FormError, Modal, Select, TextInput, useToast } from "./ui";

type State = OutputView["overlay_state"];

const POSITIONS: { value: State["logo_position"]; label: string }[] = [
  { value: "top-left", label: N_("En haut à gauche") }, { value: "top-right", label: N_("En haut à droite") },
  { value: "bottom-left", label: N_("En bas à gauche") }, { value: "bottom-right", label: N_("En bas à droite") },
];

/** Logo et texte d'une sortie : appliqués à l'antenne sans la relancer, sauf l'envoi d'un nouveau logo. */
export function OverlayModal({ output, onClose }: { output: OutputView; onClose: () => void }) {
  const toast = useToast();
  const [state, setState] = useState<State>(output.overlay_state);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const dirty = state.text !== output.overlay_state.text;

  const send = async (next: State) => {
    setState(next);
    setBusy(true);
    setError(null);
    try {
      const r = await unwrap(api.PUT("/api/outputs/{output_id}/overlay", {
        params: { path: { output_id: output.id } }, body: next }));
      toast(r.applied_live ? t("Habillage appliqué à l'antenne") : t("Habillage enregistré : il s'appliquera au démarrage de la sortie"),
        r.applied_live ? "ok" : "warn");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await unwrap(api.POST("/api/outputs/{output_id}/overlay/logo", {
        params: { path: { output_id: output.id } }, body: file as never, bodySerializer: (b: unknown) => b as BodyInit,
        headers: { "Content-Type": "image/png" } }));
      toast(t("Nouveau logo : la sortie repart"), "warn");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setFile(null);
      if (picker.current) picker.current.value = "";
    }
  };

  return (<>
    <Modal open title={t("Habillage · {name}", { name: output.name })} onClose={onClose}
      footer={<Button variant="primary" onClick={onClose}>{t("Fermer")}</Button>}>
      <div className="flex flex-col gap-5">
        <p className="text-[13px] text-muted">
          {t("Les changements partent à l'antenne en quelques instants, sans couper la sortie. Seul l'envoi d'un nouveau logo la relance (une à deux secondes).")}
        </p>

        <section className="flex flex-col gap-3">
          <h3 className="font-cond text-[12px] font-semibold uppercase tracking-wider text-faint">{t("Logo")}</h3>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <Button disabled={busy} onClick={() => picker.current?.click()}>
                {output.overlay_has_logo ? t("Remplacer le logo") : t("Importer un logo")}
              </Button>
              <span className="text-[13px] text-muted">{output.overlay_has_logo ? t("Logo en place") : t("Aucun logo importé")}</span>
            </div>
            <p className="mt-1 text-[12px] text-faint">{t("Image PNG, de préférence avec fond transparent (2 Mo maximum).")}</p>
            <input ref={picker} type="file" accept="image/png" aria-label={t("Fichier du logo")} className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <Checkbox id="ovl-logo" label={t("Afficher le logo")} checked={state.logo_visible}
            onChange={(v) => send({ ...state, logo_visible: v })} />
          <Field label={t("Position")} htmlFor="ovl-pos">
            <Select id="ovl-pos" value={state.logo_position}
              onChange={(e) => send({ ...state, logo_position: e.target.value as State["logo_position"] })}>
              {POSITIONS.map((p) => <option key={p.value} value={p.value}>{t(p.label)}</option>)}
            </Select>
          </Field>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="font-cond text-[12px] font-semibold uppercase tracking-wider text-faint">{t("Texte")}</h3>
          <Checkbox id="ovl-text-on" label={t("Afficher le texte")} checked={state.text_visible}
            onChange={(v) => send({ ...state, text_visible: v })} />
          <Field label={t("Texte (en bas à gauche)")} htmlFor="ovl-text">
            <div className="flex gap-2">
              <TextInput id="ovl-text" maxLength={200} value={state.text} className="min-w-0 flex-1"
                onChange={(e) => setState({ ...state, text: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void send(state); } }} />
              <Button disabled={busy || !dirty} onClick={() => send(state)}>{t("Appliquer")}</Button>
            </div>
          </Field>
        </section>
        <FormError message={error} />
      </div>
    </Modal>
    <ConfirmDialog open={file !== null} title={t("Envoyer ce logo ?")} confirmLabel={t("Envoyer et relancer")} busy={busy}
      onCancel={() => { setFile(null); if (picker.current) picker.current.value = ""; }} onConfirm={upload}>
      {t("La sortie « {name} » sera relancée pour prendre « {file} » : une à deux secondes de coupure à l'antenne.", { name: output.name, file: file?.name ?? "" })}
    </ConfirmDialog>
  </>);
}
